import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CLEANER_RATING_BRAND_ORANGE,
  formatCleanerListRating,
  formatReviewRatingValue,
} from '@/lib/cleaner-rating-label';

type CleanerRatingLabelProps = {
  /** Cleaner average; defaults to 5.0 when there are no reviews yet. */
  averageRating?: number | null;
  totalRatings?: number | null;
  /** Single review score (profile review rows). */
  reviewRating?: number | null;
  className?: string;
  starClassName?: string;
  valueClassName?: string;
  size?: 'sm' | 'md';
};

const SIZE_CLASSES = {
  sm: { star: 'h-3.5 w-3.5', text: 'text-sm' },
  md: { star: 'h-4 w-4', text: 'text-base' },
} as const;

export function CleanerRatingLabel({
  averageRating,
  totalRatings,
  reviewRating,
  className,
  starClassName,
  valueClassName,
  size = 'md',
}: CleanerRatingLabelProps) {
  const ratingValue =
    reviewRating != null
      ? formatReviewRatingValue(reviewRating)
      : formatCleanerListRating(averageRating, totalRatings);
  const sizeClasses = SIZE_CLASSES[size];

  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      aria-label={`${ratingValue} out of 5 stars`}
    >
      <Star
        className={cn(sizeClasses.star, starClassName)}
        style={{
          color: CLEANER_RATING_BRAND_ORANGE,
          fill: CLEANER_RATING_BRAND_ORANGE,
        }}
        aria-hidden
      />
      <span
        className={cn(
          'font-bold text-gray-900 dark:text-gray-100',
          sizeClasses.text,
          valueClassName
        )}
      >
        {ratingValue}
      </span>
    </span>
  );
}
